import { useCallback, DragEvent } from 'react';
import useSetState from 'react-use/lib/useSetState';

type Payload = {
    draggedFrom: number | null;
    draggedTo: number | null;
    isDragging: boolean;
};

const INITIAL_PAYLOAD: Payload = {
    draggedFrom: null,
    draggedTo: null,
    isDragging: false,
};

interface UseDragAndDropProps {
    onDrop: (fromIndex: number, toIndex: number) => void;
}

interface UseDragAndDropPayload {
    payload: Payload;
    onDrop: (event: DragEvent) => void;
    onDragStart: (event: DragEvent) => void;
    onDragLeave: (event: DragEvent) => void;
    onDragOver: (event: DragEvent) => void;
}

export const useDragAndDrop = (props: UseDragAndDropProps): UseDragAndDropPayload => {
    const { onDrop: handleDrop } = props;
    const [payload, setPayload] = useSetState<Payload>(INITIAL_PAYLOAD);

    const onDragStart = useCallback(
        (event: DragEvent) => {
            // @ts-ignore
            const initialPosition = Number(event.currentTarget.dataset.position);

            setPayload({
                isDragging: true,
                draggedFrom: initialPosition,
            });

            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', String(initialPosition));
        },
        [setPayload],
    );

    const onDragOver = useCallback(
        (event: DragEvent) => {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';

            // @ts-ignore
            const draggedTo = Number(event.currentTarget.dataset.position);

            if (draggedTo !== payload.draggedTo) {
                setPayload({ draggedTo });
            }
        },
        [payload, setPayload],
    );

    const onDrop = useCallback(
        (event: DragEvent) => {
            event.preventDefault();
            const draggedTo = Number((event.currentTarget as HTMLElement).dataset.position);

            setPayload(INITIAL_PAYLOAD);

            if (payload.draggedFrom !== null && Number.isInteger(draggedTo)) {
                handleDrop(payload.draggedFrom, draggedTo);
            }
        },
        [handleDrop, payload.draggedFrom, setPayload],
    );

    const onDragLeave = useCallback(() => {
        setPayload({ draggedTo: null });
    }, [setPayload]);

    return {
        payload,
        onDrop,
        onDragStart,
        onDragLeave,
        onDragOver,
    };
};
